<template>
	<div class="flex gap-2 items-center flex-col">
		<h2 class="w-full text-3xl text-center">
			{{ title || $t("Buy the full package") }}
		</h2>
		<h3 class="mb-4 pb-5 w-full text-center text-base border-b border-gray-200">
			{{ $t("We have put together a complete package for you") }}
		</h3>
		<div
			class="grid md:grid-cols-5 grid-cols-2 gap-x-4 gap-y-5 items-center w-full"
		>
			<ProductCard
				:product="currentProduct"
				picture-size="w-full h-auto flex-grow"
				content-styles="h-full"
				link-styles="h-full"
				class="min-h-[180px] h-full"
				:lazy-loading="true"
			/>
			<ProductCard
				v-for="product in products"
				:key="product.Id"
				:product="product"
				picture-size="w-full h-auto flex-grow"
				content-styles="h-full"
				link-styles="h-full"
				class="min-h-[180px] h-full"
				:lazy-loading="true"
			/>
			<div class="grid gap-2.5 col-span-2 md:col-span-1 text-center">
				<p>
					<span>{{ $t("package_products.count") }}</span>
					<span class="font-bold">
						{{ $t("package_products.units", { value: products.length + 1 }) }}
					</span>
				</p>
				<p>
					<span>{{ $t("package_products.total") }}</span>
					<span class="font-bold">
						{{ totalPrice }}
					</span>
				</p>
				<button
					class="py-3 w-full min-h-[50px] text-white md:text-base text-[15px] px-3 rounded-4xl bg-green-light hover:bg-green-600 transition-colors ease-in-out duration-200"
					:class="isProductOutOfStock && 'opacity-50 !bg-red-500'"
					:disabled="isProductOutOfStock"
					@click="handleAddAllToCart"
				>
					<div v-if="addingToCart" class="flex items-center justify-center">
						<div
							class="inline-block h-5 w-5 mr-4 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
							role="status"
						></div>
						<span>{{ $t("Adding") }}..</span>
					</div>
					<span v-else>{{ $t("package_products.add_to_cart") }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import { storeToRefs } from "pinia";

interface Props {
	products: ProductItem[];
	currentProduct: ProductItem;
	title: string;
}
const { products, currentProduct } = defineProps<Props>();
const { formatPrice } = useFormatPrice();
const cartStore = useCartStore();
const { getProductStock } = useNorcePwa();
const { updating } = storeToRefs(cartStore);

const addingToCart = ref(false);

const isProductOutOfStock = computed(() => {
	return !![...products, currentProduct].find(
		(product) => getProductStock(product) === 0
	);
});

const totalPrice = computed(() => {
	const productsTotal = products.reduce(
		(sum, product) => sum + (product.PriceIncVat || 0),
		0
	);
	const currentProductPrice = currentProduct.PriceIncVat || 0;

	return formatPrice(productsTotal + currentProductPrice);
});

const handleAddAllToCart = () => {
	addingToCart.value = true;

	const itemsToAdd = [...products, currentProduct]
		.map((product) => ({
			Id: product.Id,
			PartNo: product.PartNo,
			PriceListId: product.PriceListId,
			Quantity: 1
		}))
		.filter((product) => product.PartNo) as BasketItem[];
	cartStore.addItemsToCart(itemsToAdd);
};

watch(updating, () => {
	if (!updating.value) {
		addingToCart.value = false;
	}
});
</script>
